import { useCallback, useEffect } from 'react';
import {
  DesktopNavigationItemProps,
  NavigationLink,
  RenderChildrenFunction,
} from './NmdpSiteHeaderTypes';
import { classnames } from 'tailwindcss-classnames';
import ArrowDown from 'components/authorable/site/Header/SvgArrowDown/arrow-down';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import { menuLocation, pageNavigationDataLayerHandler } from 'lib/util/gtm-datalayer';
import { LinkField } from '@sitecore-jss/sitecore-jss-react';

const subMenu = classnames(
  'absolute',
  'bg-white',
  'border-gray-midGray',
  'max-w-screen-2xl',
  'mx-auto',
  'pb-2',
  'xl:border-b',
  'xl:break-inside-avoid-column',
  'xl:flex-col',
  'xl:px-10',
  'xl:py-4',
  'xl:shadow-lg',
  'xl:left-[-24px]',
  'xl:min-w-[368px]',
  'xl:w-max',
  'z-20',
  'mt-1'
);
const fontsize16 = 'text-base';
const dropMenuItem = classnames('block', 'w-full', 'p-4', 'xl:py-2', 'xl:px-0', 'xl:w-fit');
const desktopArrow = classnames(
  'svg-arrow-down',
  'inline-block',
  'float-right',
  'ml-2',
  'xl:float-none'
);

const getItemsForDesktop = (
  orgLinkItems: NavigationLink[] = [],
  renderChildren: RenderChildrenFunction | null = null,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _count: number | null = null
) => {
  // eslint-disable-next-line array-callback-return, consistent-return
  return orgLinkItems?.map((item: NavigationLink) => {
    // Desktop - If renderChildren is available and the item has children, render child items
    if (renderChildren && item.children) {
      return (
        <div
          key={item.id}
          className={classnames(
            'py-2',
            {
              'border-b': _count !== 0,
              'border-gray-whisper': _count !== 0,
            },
            'last:border-0'
          )}
        >
          <li
            role="menuitem"
            aria-label="Menu item"
            key={item.id}
            className={classnames(
              'bg-white',
              'block',
              'border-b',
              'border-gray-quartz',
              'pr-4',
              'mb-3'
            )}
          >
            <LinkA11yWrapper
              className={`${classnames(dropMenuItem, fontsize16)} menuitem-head xl:mb-2 xl:pb-0`}
              key={item.id}
              editable={false}
              field={item?.fields?.navigationLink as LinkField}
              suppressLinkText
              id={`nav-links--${item.id}`}
              onGtmPush={(e) => {
                pageNavigationDataLayerHandler(e, menuLocation[1]);
              }}
            >
              {item?.fields?.navigationHeading.value}
            </LinkA11yWrapper>
          </li>

          {renderChildren(item.children)}
        </div>
      );
    }
    // If the item has a navigation link, return a list item with a link
    if (item.fields?.navigationItemLink) {
      return (
        <li
          role="menuitem"
          aria-label="Menu item"
          key={item.id}
          className={classnames('bg-white', 'block', 'border-b', 'border-gray-quartz')}
        >
          <LinkA11yWrapper
            className={classnames(
              dropMenuItem,
              fontsize16,
              'xl:pt-0',
              'xl:pb-0',
              'xl:mb-2',
              'xl:mt-0'
            )}
            key={item.id}
            editable={false}
            field={item.fields.navigationItemLink}
            id={`nav-links--${item.id}`}
            onGtmPush={(e) => {
              pageNavigationDataLayerHandler(e);
            }}
          />
        </li>
      );
    }

    return null;
  });
};

function shouldManualColumnBreak(
  item: NavigationLink,
  currentColumnIndex: number,
  currentLinkGroupRow: number,
  numberOfColumns: number
) {
  return (
    !!item.fields?.columnBreak?.value &&
    currentColumnIndex < numberOfColumns - 1 &&
    currentColumnIndex >= 1 &&
    currentLinkGroupRow > 1
  );
}

function recalculateIdealItemsPerGroup(
  remainingLinkItems: number,
  numberOfColumns: number,
  currentColumnIndex: number
) {
  const remainingNumberOfColumns = numberOfColumns - (currentColumnIndex + 1);
  return remainingNumberOfColumns >= 1
    ? Math.round(remainingLinkItems / remainingNumberOfColumns)
    : remainingLinkItems;
}

function calculateColEndArray(
  orgLinkItems: NavigationLink[],
  numberOfColumns: number,
  count: number | null
): number[] {
  const colEndArray = [1, 2, 3, 4];
  if (orgLinkItems.length <= numberOfColumns) return colEndArray;

  const navCount = count ?? 0;
  let idealItemsPerGroup = Math.round(navCount / numberOfColumns);
  let currentLinkGroupIndex = 0;
  let currentColumnIndex = 0;
  let linksCounter = 0;
  let currentLinkGroupRow = 0;
  let remainingLinkItems = navCount;

  orgLinkItems.forEach((_item, i) => {
    currentLinkGroupIndex++;
    currentLinkGroupRow++;
    colEndArray[currentColumnIndex] = currentLinkGroupIndex;

    const childrenLength = orgLinkItems[i]?.children?.length || 0;
    linksCounter += childrenLength;
    remainingLinkItems -= childrenLength;

    const autoColumnBreak =
      linksCounter >= idealItemsPerGroup && currentColumnIndex < numberOfColumns;
    const manualColumnBreak = shouldManualColumnBreak(
      orgLinkItems[i],
      currentColumnIndex,
      currentLinkGroupRow,
      numberOfColumns
    );

    if (manualColumnBreak) {
      colEndArray[currentColumnIndex] = currentLinkGroupIndex - 1;
      currentColumnIndex++;
      idealItemsPerGroup = recalculateIdealItemsPerGroup(
        remainingLinkItems,
        numberOfColumns,
        currentColumnIndex
      );
      currentLinkGroupRow = 1;
      linksCounter = 0;
    } else if (autoColumnBreak) {
      currentColumnIndex++;
      linksCounter = 0;
      currentLinkGroupRow = 0;
    }
  });
  return colEndArray;
}

const getColsForDesktop = (
  orgLinkItems: NavigationLink[] = [],
  renderChildren: RenderChildrenFunction | null = null,
  count: number | null = 0
) => {
  if (count === 0) {
    return getItemsForDesktop(orgLinkItems, renderChildren, count);
  }
  const numberOfColumns = 4;
  const colEndArray = calculateColEndArray(orgLinkItems, numberOfColumns, count);

  const xlWidth25Percent = `xl:w-[25%]`;
  return (
    <>
      {colEndArray.map((end, index) => {
        const itemId = `${end}-${index}`;
        return (
          <div
            key={itemId}
            className={classnames(xlWidth25Percent, index < 3 ? 'mr-12' : undefined)}
          >
            {getItemsForDesktop(
              orgLinkItems.slice(colEndArray[index - 1] ?? 0, end),
              renderChildren,
              count
            )}
          </div>
        );
      })}
    </>
  );
};

const DesktopNavigationItem: React.FC<DesktopNavigationItemProps> = ({
  item,
  openSubMenuId,
  toggleSubMenu,
  getItemDisplayName,
}) => {
  const isSubMenuOpen = openSubMenuId === `nav-item--${item?.id}`;
  const navCount = (item.children || []).reduce(
    (acc, item2) => acc + (item2.children?.length || 0),
    0
  );
  const displayText = getItemDisplayName(item);

  const handleToggleSubMenu = useCallback(() => {
    toggleSubMenu(`nav-item--${item.id}`, displayText);
  }, [item.id, displayText, toggleSubMenu]);

  const handleDocumentClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('a') && isSubMenuOpen) {
        toggleSubMenu(null, '');
      }
    },
    [isSubMenuOpen, toggleSubMenu]
  );
  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [handleDocumentClick]);
  return (
    <>
      {item.children && item.children?.length > 0 && (
        <li
          role="menuitem"
          key={item.id}
          className={classnames({
            'submenu-open': isSubMenuOpen,
          })}
        >
          <button
            onClick={handleToggleSubMenu}
            type="button"
            className={classnames('xl:p-2', 'font-medium')}
          >
            <div className={classnames('max-h-[25px]', 'flex', fontsize16)}>
              {displayText}
              <ArrowDown aria-hidden="true" className={classnames(desktopArrow)} />
            </div>
          </button>
          <div className={subMenu} id="submenu">
            {item.fields?.navigationLink && (
              <div className={classnames('max-h-[25px]', 'md:text-[18px]', 'font-medium')}>
                <LinkA11yWrapper
                  key={item.id}
                  editable={false}
                  field={item.fields.navigationLink}
                  id={`nav-links--${item.id}`}
                  className={`${classnames('text-white', 'font-semibold')} menuitem-head`}
                  onGtmPush={(e) => {
                    pageNavigationDataLayerHandler(e);
                  }}
                />
              </div>
            )}
            <p className={classnames('text-gray-darkGray', 'w-[47.5%]', 'py-2')}>
              {item.fields?.description?.value}
            </p>
            <ul role="menu">{getColsForDesktop(item.children, getItemsForDesktop, navCount)}</ul>
          </div>
        </li>
      )}
    </>
  );
};

const getDesktopItems = (
  _orgLinkItems: NavigationLink[],
  openSubMenuId: string | null,
  toggleSubMenu: (id: string | null, displayText: string) => void,
  getItemDisplayName: (item: NavigationLink) => string
) => {
  return (_orgLinkItems || []).map((item: NavigationLink) => (
    <DesktopNavigationItem
      key={item.id}
      item={item}
      openSubMenuId={openSubMenuId}
      toggleSubMenu={toggleSubMenu}
      getItemDisplayName={getItemDisplayName}
    />
  ));
};

export default getDesktopItems;
